import { getToken } from './BearerToken';

/**
 * The AgentCore Runtime transport: one POST, one SSE stream, typed events.
 *
 * Pulled out of `ChatAgentService` when the booking AI overview became the
 * second consumer. It is the same request and the same frame parsing for
 * both, and two copies of an SSE reader is how one of them quietly stops
 * handling a trailing frame.
 *
 * WHY THIS TALKS TO BEDROCK AND NOT TO OUR OWN API. Streaming is the whole
 * point and our API cannot do it: `PickupHungaryDashApiGateway` is a REST
 * API v1, and REST APIs do not support Lambda response streaming. The
 * runtime is invoked directly from the browser with the operator's Cognito
 * JWT — no API Gateway in the path — which is why a response can arrive a
 * token at a time.
 *
 * Auth: the Cognito JWT goes as `Authorization: Bearer <token>`; the
 * AgentCore-side authorizer verifies it.
 *
 * Session: the runtime requires a session id of >=33 chars per invocation
 * and does NOT support concurrent invocations on one session. Callers that
 * share a session must serialise; callers that do not care about continuity
 * should use a fresh id, which is what the overview does.
 */

const REGION = process.env.REACT_APP_AGENTCORE_REGION;
const RUNTIME_ARN = process.env.REACT_APP_AGENTCORE_RUNTIME_ARN;
// Local-dev override: when set, callers POST directly to this URL
// (e.g. http://localhost:8080/invocations) instead of constructing the
// AWS-hosted AgentCore endpoint. Lets us iterate on the agent container
// without redeploying. Leave unset in prod.
const LOCAL_URL = process.env.REACT_APP_AGENTCORE_LOCAL_URL;

// Bedrock's invocation route is `/runtimes/{agentRuntimeArn}/invocations`
// and AWS requires the FULL ARN in that slot when the caller authenticates
// via Cognito JWT (no SigV4 → no account context on the request, so a
// bare id can't be resolved to an ARN — server returns
// "accountID is required when agentRuntimeArn is provided as agentId").
// We pass the ARN through verbatim, percent-encoded.
export const buildEndpointUrl = () => {
  if (LOCAL_URL) return LOCAL_URL;
  if (!REGION) {
    throw new Error('REACT_APP_AGENTCORE_REGION is not set — cannot invoke chat agent');
  }
  if (!RUNTIME_ARN) {
    throw new Error('REACT_APP_AGENTCORE_RUNTIME_ARN is not set — cannot invoke chat agent');
  }
  if (!RUNTIME_ARN.startsWith('arn:')) {
    throw new Error(
      'REACT_APP_AGENTCORE_RUNTIME_ARN must be a full ARN '
      + '(arn:aws:bedrock-agentcore:<region>:<account>:runtime/<name>) — '
      + 'JWT-authenticated invocations cannot resolve a bare id.',
    );
  }
  return `https://bedrock-agentcore.${REGION}.amazonaws.com/runtimes/${encodeURIComponent(RUNTIME_ARN)}/invocations?qualifier=DEFAULT`;
};

/** A session id the runtime will accept: >=33 chars, unique per call. */
export const newSessionId = (prefix) => {
  const rand = () => Math.random().toString(36).slice(2)
    + Math.random().toString(36).slice(2);
  return `${prefix}-${Date.now().toString(36)}-${rand()}${rand()}`;
};

/**
 * POST `body` and hand each decoded SSE event to `onEvent`.
 *
 * Resolves when the stream ends. Throws on a non-2xx, a missing body, or a
 * transport fault; an abort resolves quietly, because a caller that aborted
 * already knows.
 */
export const invokeStream = async ({
  body, sessionId, signal, onEvent, token,
}) => {
  // The CALLER may supply the token, because which token is the caller's
  // decision. The dashboard's own session token is the default; the driver
  // portal keeps a dispatcher's id token under its own key and renews it
  // before an invocation, so `ChatAgentService` resolves its injected
  // getter and passes the result in. Reaching for `getToken()` here would
  // put the admin token on a portal request.
  const bearer = token ?? getToken();
  if (!bearer) {
    throw new Error('No bearer token available — cannot invoke the agent runtime');
  }
  if (!sessionId || sessionId.length < 33) {
    throw new Error('AgentCore runtime requires a session id of at least 33 characters');
  }

  const emit = onEvent ?? (() => {});
  let response;
  try {
    response = await fetch(buildEndpointUrl(), {
      method: 'POST',
      signal,
      headers: {
        Authorization: `Bearer ${bearer}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId,
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    if (err.name === 'AbortError') return { aborted: true };
    throw err;
  }

  if (!response.ok) {
    let detail = '';
    try {
      detail = await response.text();
    } catch {
      /* ignore */
    }
    throw new Error(`AgentCore invocation failed (${response.status}): ${detail || response.statusText}`);
  }

  if (!response.body) {
    throw new Error('AgentCore response did not include a readable stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handleFrame = (frame) => {
    const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
    if (!dataLine) return;
    const json = dataLine.slice(5).trim();
    if (!json) return;
    try {
      emit(JSON.parse(json));
    } catch {
      // Swallow malformed frames — the stream may still have valid
      // frames queued behind them.
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by \n\n. Keep the last partial frame in
      // `buffer` for the next iteration.
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      parts.forEach(handleFrame);
    }
    // Flush any trailing frame that lacked a closing \n\n.
    const trailing = buffer.trim();
    if (trailing) handleFrame(trailing);
  } catch (err) {
    if (err.name === 'AbortError') return { aborted: true };
    throw err;
  }

  return { aborted: false };
};

export default { invokeStream, buildEndpointUrl, newSessionId };
